import { RootStore } from "app/rootStore";
import { AgentAccountChecked, DefaultChecked } from "app/spaceStone/signUp/steps/interfaces/signUpFinishChecked";
import { AxiosError } from "axios";
import { CompanyDetails } from "components/userDetails/interfaces/companyDetails";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { requestCodeVerification, requestPhoneVerification } from "shared/api/auth/phoneVerificationApi";
import { loginByToken } from "shared/api/auth/tokenApi";
import { resendSignUpLink, startSignUp } from "shared/api/spaceStone/accountApi";
import { verifyCompanyRegistrationNumber } from "shared/api/spaceStone/agencyApi";
import {
    registerAgent,
    registerCorporateLandlord,
    registerInternalUser,
    registerPrivateLandlord,
    registerTenant,
} from "shared/api/spaceStone/signUpApi";
import { AccountType } from "shared/constants/enums";
import { navigateOnExpiredOrErrorPage } from "shared/helpers";
import { RequestError } from "shared/interfaces/errors/RequestError";
import { SignUpFinishError } from "shared/interfaces/errors/SignUpFinishError";
import { AddressesLookup } from "shared/models/spaceStone/lookup/addressesLookup";
import {
    RegisterAgent,
    RegisterCorporateLandlord,
    RegisterInternalUser,
    RegisterPrivateLandlord,
    RegisterTenant,
} from "shared/models/spaceStone/signUp";
import routeNames from "shared/routes/constants/routeNames";
import { navigateOnContinueSignUp, navigateOnSuccessLogin, navigateToUrl } from "shared/routes/routes.utils";
import { showErrorToast, showSuccessToast } from "uiKit/container/toast/helpers";
import { formatPhoneNumber } from "shared/utils/phone.utils";
import { InviteProfileData } from "shared/interfaces/signUpViaInvite";
import { isLandlord } from "app/spaceStone/signUp/steps/continueSignUp/landlord/helpers";
import { pickServerValidationCode } from "shared/api/utils";

interface SignUpStoreInterface {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    activeStep: number;
    sendLinkStep: number;
    register: (email: string) => void;
    resendLink: (toastMessage: string) => void;

    clearStore(): void;

    userAccountType: AccountType;
    companyStep: number;
}

type SetCompanyDetailsFunction = (data: CompanyDetails) => void;

const LAST_SIGN_UP_STEP = 4;
const DEACTIVATED_USER_ERROR_CODE = "YouAreUnableToSignUp";

export class SignUpStore implements SignUpStoreInterface {
    @observable public email = "";
    @observable public firstName = "";
    @observable public lastName = "";
    @observable public phone = "";
    @observable public activeStep = 0;
    @observable public sendLinkStep = 1;
    @observable public userAccountType = AccountType.None;
    @observable public companyId: null | number = null;
    @observable public returnUrl: null | string = null;
    @observable public isInvitation = false;
    @observable public signUpToken = "";
    @observable public invitationToken = "";
    @observable public deactivatedUserError = false;
    @observable public isPredefinedUserAccountType = false;
    @observable public formState: { errors: string[] } = {
        errors: [],
    };

    @observable public companyStep = 1;

    private getEmptyCompanyDetails = (): CompanyDetails => ({
        companyRegistrationNumber: "",
        isSoleTraderOrLimitedCompany: false,
        companyName: "",
        tradingName: "",
        photoBase64: null,
    });

    private getEmptyCompanyAddress = (): AddressesLookup => ({
        addressLine1: "",
        addressLine2: "",
        postcode: "",
        city: "",
        country: undefined,
        latitude: undefined,
        longitude: undefined,
        uniquePropertyReferenceNumber: "",
    });

    @observable public agentCompanyDetails: CompanyDetails = this.getEmptyCompanyDetails();
    @observable public corporateLandlordCompanyDetails: CompanyDetails = this.getEmptyCompanyDetails();

    @observable public agentCompanyAddress: AddressesLookup = this.getEmptyCompanyAddress();
    @observable public corporateLandlordCompanyAddress: AddressesLookup = this.getEmptyCompanyAddress();

    @observable public agentAccountFinish: AgentAccountChecked = {
        sendNews: false,
        privacyPolicy: false,
        checkedHonestyQuestions: [],
    };

    @observable public tenantAccountFinish: DefaultChecked = {
        sendNews: false,
        privacyPolicy: false,
    };

    @observable public corporateLandlordFinish: DefaultChecked = {
        sendNews: false,
        privacyPolicy: false,
    };

    @observable public privateLandlordFinish: DefaultChecked = {
        sendNews: false,
        privacyPolicy: false,
    };

    @observable public defaultFinish: DefaultChecked = {
        sendNews: false,
        privacyPolicy: false,
    };

    public rootStore: RootStore;
    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action.bound
    public goToLastStep(): void {
        this.activeStep = LAST_SIGN_UP_STEP;
    }

    @action.bound
    public setupSignUpToken(token: string): void {
        this.signUpToken = token;
    }

    @action.bound
    public setProfileDataViaInvitation(
        profileData: InviteProfileData,
        returnUrl: string | null,
        invitationToken: string
    ): void {
        const { firstName, lastName, accountTypeId, companyId } = profileData;
        this.setFirstAndLastName(firstName || "", lastName || "");
        this.rootStore.setAccountTypeId = accountTypeId;
        this.invitationToken = invitationToken;
        this.userAccountType = accountTypeId;
        this.companyId = companyId;
        this.isInvitation = true;
        this.returnUrl = returnUrl;
    }

    @action.bound
    public setIsDoNotCheckCompanyNumber(isDoNotCheck: boolean): void {
        const companyDetails =
            this.userAccountType === AccountType.Agent
                ? this.agentCompanyDetails
                : this.corporateLandlordCompanyDetails;
        companyDetails.isSoleTraderOrLimitedCompany = isDoNotCheck;
    }

    @action.bound
    public setNextCompanyStep(): void {
        this.companyStep += 1;
        this.formState.errors = [];
    }

    @action.bound
    public setPreviousCompanyStep(): void {
        this.companyStep -= 1;
        this.formState.errors = [];
    }

    @action.bound
    public setAgentCompanyDetails(companyDetails: CompanyDetails): void {
        this.agentCompanyDetails = companyDetails;
    }

    @action.bound
    public setCorporateLandlordCompanyDetails(companyDetails: CompanyDetails): void {
        this.corporateLandlordCompanyDetails = companyDetails;
    }

    @action.bound
    public setAgentCompanyAddress(companyAddress: AddressesLookup | null): void {
        if (!companyAddress) {
            return;
        }
        this.agentCompanyAddress = this.getCorrectAddress(companyAddress);
    }

    @action.bound
    public setCorporateLandlordCompanyAddress(companyAddress: AddressesLookup | null): void {
        if (!companyAddress) {
            return;
        }
        this.corporateLandlordCompanyAddress = this.getCorrectAddress(companyAddress);
    }

    @action.bound
    public setAgentAccountFinish(agentAccountChecked: AgentAccountChecked): void {
        this.agentAccountFinish = agentAccountChecked;
    }

    @action.bound
    public setTenantAccountFinish(defaultChecked: DefaultChecked): void {
        this.tenantAccountFinish = defaultChecked;
    }

    @action.bound
    public setCorporateLandlordAccountFinish(defaultChecked: DefaultChecked): void {
        this.corporateLandlordFinish = defaultChecked;
    }

    @action.bound
    public setPrivateLandlordAccountFinish(defaultChecked: DefaultChecked): void {
        this.privateLandlordFinish = defaultChecked;
    }

    @action.bound
    public setDefaultFinish(defaultChecked: DefaultChecked): void {
        this.defaultFinish = defaultChecked;
    }

    @action.bound
    public setEmail(payload: string): void {
        this.email = payload;
    }

    @action.bound
    public setFirstAndLastName(firstName: string, lastName: string): void {
        this.firstName = firstName;
        this.lastName = lastName;
    }

    @action.bound
    public setPhone(phone: string): void {
        this.phone = phone;
    }

    @action.bound
    public shouldSkipStepperStep(accountType: AccountType = this.userAccountType): boolean {
        return [AccountType.Tenant, AccountType.PrivateLandlord].includes(accountType);
    }

    @action.bound
    public shouldSkipToFinalStep(): boolean {
        return !!this.companyId || this.userAccountType === AccountType.Tenant;
    }

    @action.bound
    public moveToNextStep(shouldSkipOneStep = false): void {
        if (shouldSkipOneStep) {
            this.activeStep += 2;
        } else {
            this.activeStep += 1;
        }
    }

    @action.bound
    public moveToPrevStep(shouldDoDoubleBack = false): void {
        if (shouldDoDoubleBack) {
            this.activeStep -= 2;
        } else {
            this.activeStep -= 1;
        }
    }

    @action.bound
    public moveToNextSendLinkStep(): void {
        this.sendLinkStep += 1;
    }

    @action.bound
    public moveToPrevSendLinkStep(): void {
        this.sendLinkStep -= 1;
    }

    @action.bound
    public clearErrors(): void {
        this.deactivatedUserError = false;
        this.formState.errors = [];
    }

    @action.bound
    public clearStore(): void {
        this.clearErrors();
        this.email = "";
        this.firstName = "";
        this.lastName = "";
        this.activeStep = 0;
        this.sendLinkStep = 1;
    }

    private handleError(error: AxiosError, saveMessage?: boolean) {
        const { response } = error;
        if (
            response &&
            response.status === 400 &&
            response.data &&
            response.data.errors &&
            response.data.errors.length
        ) {
            runInAction(() =>
                this.formState.errors.push(saveMessage ? response.data.errors[0].message : response.data.errors[0].code)
            );
        } else {
            runInAction(() => this.formState.errors.push(error.code || ""));
        }
    }

    private showErrors = (errorObject: RequestError) => {
        if (errorObject.response?.data && errorObject.response.data?.errors?.length > 0) {
            errorObject.response.data.errors.forEach((errItem: SignUpFinishError) =>
                showErrorToast({ message: errItem.message })
            );
        }
    };

    @action.bound
    public setRole(accountType: AccountType): void {
        this.userAccountType = accountType;
    }

    @action.bound
    public async resendLink(toastMessage?: string): Promise<void> {
        try {
            await resendSignUpLink(this.email);

            if (toastMessage) {
                showSuccessToast({ message: toastMessage });
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
        }
    }

    @action.bound
    public async requestPhoneVerification(phone: string, toastMessage?: string): Promise<void> {
        try {
            this.clearErrors();
            await requestPhoneVerification(phone);

            if (toastMessage) {
                showSuccessToast({ message: toastMessage });
            }

            runInAction(() => {
                this.setPhone(phone);
            });
        } catch (error) {
            this.handleError(error);
        }
    }

    @action.bound
    public async requestCodeVerification(code: string): Promise<void> {
        try {
            this.clearErrors();
            const result = await requestCodeVerification(this.phone, code);
            if (result) {
                let shouldSkip = false;
                if (this.isInvitation) {
                    if (this.shouldSkipToFinalStep()) {
                        return this.goToLastStep();
                    }
                    if (!isLandlord(this.userAccountType) && !(this.userAccountType === AccountType.Admin)) {
                        shouldSkip = true;
                    }
                }
                if (this.rootStore.isTenant) {
                    return this.goToLastStep();
                }
                this.moveToNextStep(shouldSkip);
            }
        } catch (error) {
            this.handleError(error);
        }
    }

    @action.bound
    public async verifyCompanyRegistrationNumber(
        registrationNumber: string,
        setDetails: SetCompanyDetailsFunction,
        data: CompanyDetails
    ): Promise<void> {
        try {
            this.clearErrors();
            const result = await verifyCompanyRegistrationNumber(registrationNumber);
            if (result.isSucceeded) {
                setDetails(data);
                this.setNextCompanyStep();
            }
        } catch (error) {
            this.handleError(error, true);
        }
    }

    @action.bound
    public async registerAgent(): Promise<void> {
        try {
            const token = await registerAgent(this.getRegisterModel() as RegisterAgent);
            runInAction(() => {
                this.updateToken(token);
            });
        } catch (error) {
            this.showErrors(error);
            throw error;
        }
    }

    @action.bound
    public async registerCorporateLandlord(): Promise<void> {
        try {
            const token = await registerCorporateLandlord(this.getRegisterModel() as RegisterCorporateLandlord);
            runInAction(() => {
                this.updateToken(token);
            });
        } catch (error) {
            this.showErrors(error);
            throw error;
        }
    }

    @action.bound
    public async registerTenant(): Promise<void> {
        try {
            const token = await registerTenant(this.getRegisterModel() as RegisterTenant);
            runInAction(() => {
                this.updateToken(token);
            });
        } catch (error) {
            this.showErrors(error);
            throw error;
        }
    }

    @action.bound
    public async registerPrivateLandlord(): Promise<void> {
        try {
            const token = await registerPrivateLandlord(this.getRegisterModel() as RegisterPrivateLandlord);
            runInAction(() => {
                this.updateToken(token);
            });
        } catch (error) {
            this.showErrors(error);
            throw error;
        }
    }

    @action.bound
    public async registerInternalUser(): Promise<void> {
        try {
            const token = await registerInternalUser(this.getRegisterModel() as RegisterInternalUser);
            runInAction(() => {
                this.updateToken(token);
            });
        } catch (error) {
            this.showErrors(error);
            throw error;
        }
    }

    @action.bound
    public async register(email: string): Promise<void> {
        try {
            this.clearErrors();
            await startSignUp(email);
            runInAction(() => {
                this.email = email;
            });
        } catch (error) {
            const errorCode = pickServerValidationCode(error);
            if (errorCode === DEACTIVATED_USER_ERROR_CODE) {
                this.deactivatedUserError = true;
                return;
            }

            this.handleError(error);
        }
    }

    @action.bound
    async loginByToken(token: string): Promise<void> {
        try {
            const response = await loginByToken(token);
            this.rootStore.authStore.setAuthData(response);
            navigateOnContinueSignUp(this.rootStore);
            this.setPredefinedUserAccountType();
        } catch (error) {
            this.clearStore();
            navigateOnExpiredOrErrorPage(error, this.rootStore.routerStore, routeNames.ERRORS.EXPIRED_REG_LINK, token);
            runInAction(() => this.errors.push(error));
        }
    }

    @action.bound
    setPredefinedUserAccountType(): void {
        if (this.rootStore.isTenant) {
            this.userAccountType = AccountType.Tenant;
            this.isPredefinedUserAccountType = true;
        }

        if (this.rootStore.isPrivateLandlord) {
            this.userAccountType = AccountType.PrivateLandlord;
            this.isPredefinedUserAccountType = true;
        }
    }

    @action.bound
    async updateToken(token: string): Promise<void> {
        const response = await loginByToken(token);
        this.rootStore.authStore.setAuthData(response);
        this.navigateOnSuccessLogin();
    }

    @computed
    public get emailTitle(): string {
        return this.email ? this.email : "";
    }

    @computed
    public get errors(): string[] {
        return this.formState.errors;
    }

    @computed
    public get hasErrors(): boolean {
        return this.formState.errors.length > 0;
    }

    @computed
    public get isDoNotCheckCompanyNumber(): boolean {
        if (this.userAccountType === AccountType.Agent) {
            return this.agentCompanyDetails.isSoleTraderOrLimitedCompany;
        }
        return this.corporateLandlordCompanyDetails.isSoleTraderOrLimitedCompany;
    }

    @computed
    public get phoneNumber(): string | undefined {
        return formatPhoneNumber(this.phone);
    }

    private getCorrectAddress = (address: AddressesLookup): AddressesLookup => ({
        ...address,
        addressLine1: address.addressLine1 ?? "",
        addressLine2: address.addressLine2 ?? "",
        city: address.city ?? "",
        postcode: address.postcode ?? "",
        latitude: address.latitude ?? undefined,
        longitude: address.longitude ?? undefined,
        uniquePropertyReferenceNumber: address.uniquePropertyReferenceNumber ?? "",
    });

    private getRegisterModel() {
        let marketingPreferences = false;

        switch (this.userAccountType) {
            case AccountType.Agent:
                marketingPreferences = this.agentAccountFinish.sendNews;
                break;
            case AccountType.CorporateLandlord:
                marketingPreferences = this.corporateLandlordFinish.sendNews;
                break;
            case AccountType.PrivateLandlord:
                marketingPreferences = this.privateLandlordFinish.sendNews;
                break;
            case AccountType.Tenant:
                marketingPreferences = this.tenantAccountFinish.sendNews;
                break;
            case AccountType.Admin:
            default:
                marketingPreferences = false;
        }

        const isAgent = this.userAccountType === AccountType.Agent;

        const commonUserModel = {
            firstName: this.firstName,
            lastName: this.lastName,
            marketingPreferences,
            phone: this.phone,
        };

        const adminUserModel = {
            firstName: this.firstName,
            lastName: this.lastName,
            phone: this.phone,
        };

        const companyDetails = isAgent ? this.agentCompanyDetails : this.corporateLandlordCompanyDetails;
        const companyAddress = isAgent ? this.agentCompanyAddress : this.corporateLandlordCompanyAddress;

        const commonCompanyModel = {
            companyName: companyDetails.companyName,
            companyRegistrationNumber: companyDetails.companyRegistrationNumber,
            tradingName: companyDetails.tradingName,
            centralPhoneNumber: companyDetails.centralPhone,
            centralEmail: companyDetails.centralEmail,
            address: {
                postcode: companyAddress.postcode,
                addressLine1: companyAddress.addressLine1,
                addressLine2: companyAddress.addressLine2,
                city: companyAddress.city,
                country: { id: companyAddress.country ? companyAddress.country.id : 0 },
                latitude: companyAddress.latitude,
                longitude: companyAddress.longitude,
            },
            isSoleTraderOrLimitedCompany: companyDetails.isSoleTraderOrLimitedCompany,
        };

        switch (this.userAccountType) {
            case AccountType.PrivateLandlord:
            case AccountType.Tenant:
                return {
                    userModel: commonUserModel,
                    invitationToken: this.invitationToken || null,
                    companyId: this.companyId || null,
                };
            case AccountType.Agent:
                return {
                    agent: commonUserModel,
                    agency: !this.invitationToken
                        ? {
                              ...commonCompanyModel,
                              honestyQuestions: this.agentAccountFinish.checkedHonestyQuestions,
                          }
                        : null,
                    invitationToken: this.invitationToken || null,
                    companyId: this.companyId || null,
                };
            case AccountType.Admin:
                return {
                    userModel: adminUserModel,
                    invitationToken: this.invitationToken,
                };
            case AccountType.CorporateLandlord:
                return {
                    userModel: commonUserModel,
                    company: this.companyId ? null : commonCompanyModel,
                    invitationToken: this.companyId ? this.invitationToken : null,
                    companyId: this.companyId || null,
                };
            default:
                return {
                    userModel: commonUserModel,
                    company: this.companyId ? null : commonCompanyModel,
                    invitationToken: this.invitationToken || null,
                    companyId: this.companyId || null,
                };
        }
    }

    @action.bound
    private navigateOnSuccessLogin() {
        if (this.returnUrl) {
            navigateToUrl(this.rootStore, this.returnUrl);
        } else {
            navigateOnSuccessLogin(this.rootStore);
        }

        this.returnUrl = null;
    }
}
